/**
 * Settings panel — fixed overlay on right side (position: fixed, no canvas resize).
 * Toggle with Escape key or gear button on HUD.
 * Tabs: VIDEO, AUDIO, CONTROLS, DESIGN.
 * Game continues running at full canvas size behind the panel.
 *
 * Performance: Panel uses position: fixed, which removes it from document flow.
 * The canvas stays at full viewport width — zero resize, zero framebuffer
 * reallocation in the postprocessing pipeline. Matches DebugPanel's pattern
 * of overlaying without affecting canvas layout.
 *
 * Export/Import buttons are global (visible on all tabs) at the panel bottom.
 * They export/import a unified settings envelope covering all stores.
 */

import { memo, useCallback, useEffect, useRef, useState } from 'react';
import { VideoTab } from './settings/VideoTab';
import { AudioTab } from './settings/AudioTab';
import { ControlsTab } from './settings/ControlsTab';
import { DesignTab } from './settings/DesignTab';
import { exportAllSettings, importAllSettings } from '../../stores/settingsExport';
import styles from './SettingsPanel.module.css';

type TabId = 'video' | 'audio' | 'controls' | 'design';

const TABS: { id: TabId; label: string }[] = [
  { id: 'video', label: 'VIDEO' },
  { id: 'audio', label: 'AUDIO' },
  { id: 'controls', label: 'CONTROLS' },
  { id: 'design', label: 'DESIGN' },
];

interface SettingsPanelProps {
  readonly visible: boolean;
  readonly onClose: () => void;
  readonly onOpenParticleEditor?: () => void;
}

export const SettingsPanel = memo(function SettingsPanel({ visible, onClose, onOpenParticleEditor }: SettingsPanelProps) {
  const [activeTab, setActiveTab] = useState<TabId>('design');
  const [importError, setImportError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Close on Escape key
  useEffect(() => {
    if (!visible) return;
    function handleKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        onClose();
      }
    }
    window.addEventListener('keydown', handleKeyDown, true);
    return () => window.removeEventListener('keydown', handleKeyDown, true);
  }, [visible, onClose]);

  // Native event listeners on the panel element — stops camera zoom.
  // Must be native (not React onWheel) because the camera zoom uses a native
  // window-level listener. stopPropagation prevents bubbling to window.
  const panelRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = panelRef.current;
    if (!visible || !el) return;
    function stopEvent(e: Event) {
      e.stopPropagation();
    }
    el.addEventListener('wheel', stopEvent, { passive: true });
    el.addEventListener('pointerdown', stopEvent);
    el.addEventListener('pointerup', stopEvent);
    el.addEventListener('contextmenu', stopEvent);
    return () => {
      el.removeEventListener('wheel', stopEvent);
      el.removeEventListener('pointerdown', stopEvent);
      el.removeEventListener('pointerup', stopEvent);
      el.removeEventListener('contextmenu', stopEvent);
    };
  }, [visible]);

  // ---- Export ----
  const handleExport = useCallback(() => {
    const json = exportAllSettings();
    const blob = new Blob([json], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'warmancer-settings.json';
    a.click();
    URL.revokeObjectURL(url);
  }, []);

  // ---- Import ----
  const handleImportClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const handleFileChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      const error = importAllSettings(reader.result as string);
      setImportError(error);
    };
    reader.readAsText(file);
    // Reset input so the same file can be re-imported
    e.target.value = '';
  }, []);

  if (!visible) return null;

  return (
    <div ref={panelRef} className={styles.panel}>
      <div className={styles.header}>
        <span className={styles.title}>Settings</span>
        <button
          type="button"
          className={styles.closeButton}
          onClick={onClose}
          aria-label="Close settings"
        >
          &times;
        </button>
      </div>

      <div className={styles.tabs}>
        {TABS.map((tab) => (
          <button
            key={tab.id}
            type="button"
            className={activeTab === tab.id ? styles.tabActive : styles.tab}
            onClick={() => setActiveTab(tab.id)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      <div className={styles.content}>
        {activeTab === 'video' && <VideoTab />}
        {activeTab === 'audio' && <AudioTab />}
        {activeTab === 'controls' && <ControlsTab />}
        {activeTab === 'design' && <DesignTab onOpenParticleEditor={onOpenParticleEditor} />}
      </div>

      <div className={styles.panelActions}>
        <button type="button" className={styles.panelActionButton} onClick={handleExport}>
          Export JSON
        </button>
        <button type="button" className={styles.panelActionButton} onClick={handleImportClick}>
          Import JSON
        </button>
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          className={styles.importInput}
          onChange={handleFileChange}
        />
      </div>
      {importError && <div className={styles.importError}>{importError}</div>}
    </div>
  );
});
